package org.zq.astrock_ai.storage

/** 当前设备上的自选股代码持久化。 */
expect object WatchlistLocalStorage {
    fun load(): List<String>
    fun save(codes: List<String>)
}

/** 每日预测及到期复盘记录。 */
expect object AdaptiveLearningStorage {
    fun load(): String?
    fun save(raw: String)
}

/** 上次自选扫描信号，用于对比「新买/新卖/转买/转卖」。 */
expect object QuantScanSignalStorage {
    fun load(): Map<String, String>
    fun save(signals: Map<String, String>)
}

/** 扫描历史快照（JSON 字符串）。 */
expect object QuantScanHistoryStorage {
    fun load(): String?
    fun save(raw: String)
}

/** 扫描提醒与定时复扫偏好。 */
data class QuantScanPrefs(
    val alertEnabled: Boolean = true,
    /** 0=关闭；仅允许 5 / 15 */
    val autoRescanMinutes: Int = 0,
)

expect object QuantScanPrefsStorage {
    fun load(): QuantScanPrefs
    fun save(prefs: QuantScanPrefs)
}

/** 持仓列表原始串（含可选提醒阈值）。 */
expect object HoldingsLocalStorage {
    fun load(): String?
    fun save(raw: String)
}

/** 通用本机键值（分组、聊天、纸面流水等）。 */
expect object ResearchKvStorage {
    fun get(key: String): String?
    fun put(key: String, value: String)
}

internal fun encodeScanPrefs(prefs: QuantScanPrefs): String {
    val minutes = when (prefs.autoRescanMinutes) {
        5, 15 -> prefs.autoRescanMinutes
        else -> 0
    }
    return "alert=${if (prefs.alertEnabled) 1 else 0};rescan=$minutes"
}

internal fun decodeScanPrefs(raw: String?): QuantScanPrefs {
    if (raw.isNullOrBlank()) return QuantScanPrefs()
    var alert = true
    var rescan = 0
    raw.split(';').forEach { part ->
        val idx = part.indexOf('=')
        if (idx <= 0) return@forEach
        val key = part.substring(0, idx).trim()
        val value = part.substring(idx + 1).trim()
        when (key) {
            "alert" -> alert = value != "0" && !value.equals("false", ignoreCase = true)
            "rescan" -> rescan = value.toIntOrNull()?.takeIf { it == 5 || it == 15 } ?: 0
        }
    }
    return QuantScanPrefs(alertEnabled = alert, autoRescanMinutes = rescan)
}

internal fun decodeSignalMap(raw: String?): Map<String, String> {
    if (raw.isNullOrBlank()) return emptyMap()
    return raw.split(';')
        .mapNotNull { part ->
            val idx = part.indexOf('=')
            if (idx <= 0) return@mapNotNull null
            val code = part.substring(0, idx).filter { it.isDigit() }.takeLast(6)
            val signal = part.substring(idx + 1).trim().uppercase()
            if (code.length == 6 && signal.isNotBlank()) code to signal else null
        }
        .toMap()
}

internal fun encodeSignalMap(signals: Map<String, String>): String =
    signals.entries
        .sortedBy { it.key }
        .joinToString(";") { "${it.key}=${it.value}" }

internal fun decodeWatchlist(raw: String?): List<String> =
    raw.orEmpty()
        .split(',')
        .map(String::trim)
        .filter { code -> code.length == 6 && code.all(Char::isDigit) }
        .distinct()

internal fun encodeWatchlist(codes: List<String>): String =
    codes
        .filter { code -> code.length == 6 && code.all(Char::isDigit) }
        .distinct()
        .joinToString(",")
